/* tslint:disable:object-literal-key-quotes */
import { triggerPublicEvent } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import { PlayerEventCallbackFn } from './video';

export interface IInitYoutubeReturn {
	remove: () => void;
	addEventCallback: (callbackFn: PlayerEventCallbackFn) => void;
}

interface IYoutubePlayerStateChangeData {
	data: -1 | 0 | 1 | 2 | 3 | 5;
}

export const initYoutube = async (videoContext: GondelJqueryComponent): Promise<IInitYoutubeReturn> => {
	const playerData = videoContext.$ctx.data();
	const playerCallbacks: PlayerEventCallbackFn[] = [];
	let player;
	let progress: 0 | 25 | 50 | 75 | 100 = 0;
	let videoLength = 0;
	let videoTitle: string;
	let progressInterval;

	const onYoutubeApiLoaded = () => {
		const YT = (window as any).YT as any;

		player = new YT.Player(playerData.playerContainerId, {
			videoId: playerData.srcVideoid,
			playerVars: {
				enablejsapi: 1,
				loop: playerData.srcLoop,
				start: playerData.srcStart,
				end: playerData.srcEnd,
				controls: playerData.srcControls,
				mute: playerData.srcMute,
				autoplay: playerData.autoplay,
			},
			events: {
				onReady: onPlayerReady,
				onStateChange: onPlayerStateChange,
			},
		});
	};

	const onPlayerReady = () => {
		if (playerData.loop) {
			player.seekTo(playerData.srcStart);

			if (playerData.autoplay) {
				player.playVideo();
			}
		}

		videoLength = getVideoLength();
		videoTitle = player.getVideoData().title;
	};

	const getVideoLength = (): number => {
		const videoStart = playerData.srcStart || 0;
		const videoEnd = playerData.srcEnd || player.getDuration();
		return videoEnd - videoStart;
	};

	const onPlayerStateChange = (event: IYoutubePlayerStateChangeData) => {
		const YT = (window as any).YT as any;

		switch (event.data) {
			case YT.PlayerState.PLAYING: {
				// in case, the user replays the video, we need to set a potential srcStart, as there is no specific
				// event, we need to do it here
				if (progress === 100 && playerData.srcStart) {
					player.seekTo(playerData.srcStart);
				}

				// in case, the user replays the video, we need reset the progress to fire the progress events again
				if (progress === 100) {
					progress = 0;
				}

				triggerCustomEventCallback(EventNames.VideoPlay);
				progressInterval = setInterval(trackProgress, 1000);
				break;
			}
			case YT.PlayerState.PAUSED: {
				triggerCustomEventCallback(EventNames.VideoStop);
				clearInterval(progressInterval);
				break;
			}
			case YT.PlayerState.ENDED: {
				triggerCustomEventCallback(EventNames.VideoComplete);
				clearInterval(progressInterval);
				progress = 100;
				checkForVideoLoopAndRestart();
				break;
			}
		}
	};

	const triggerCustomEventCallback = (eventName: EventNames, includeProgress: boolean = false) => {
		playerCallbacks.forEach((playerCallback) => {
			playerCallback(
				'youtube',
				eventName,
				playerData.srcVideoid,
				videoTitle,
				includeProgress ? progress : undefined
			);
		});
	};

	const checkForVideoLoopAndRestart = () => {
		if (playerData.srcLoop) {
			player.loadVideoById({
				videoId: playerData.srcVideoid,
				startSeconds: playerData.srcStart,
				endSeconds: playerData.srcEnd,
			});
		}
	};

	const trackProgress = () => {
		// return if we have already fired all three progress events
		if (progress === 75) {
			return;
		}

		// currentTime needs to be absolute, if the video should not start from the begin, we need to subtract the startTime
		const currentTime = player.getCurrentTime() - (playerData.srcStart || 0);
		const currentPercentage = Math.floor((currentTime / videoLength) * 100);

		// fire progress event if we just crossed a threshold of 25/50/75 percent
		if (currentPercentage >= 25 && progress === 0) {
			progress = 25;
			triggerCustomEventCallback(EventNames.VideoProgress, true);
		}
		if (currentPercentage >= 50 && progress === 25) {
			progress = 50;
			triggerCustomEventCallback(EventNames.VideoProgress, true);
		}
		if (currentPercentage >= 75 && progress === 50) {
			progress = 75;
			triggerCustomEventCallback(EventNames.VideoProgress, true);
		}
	};

	triggerPublicEvent('gLoadYoutubeApiHandler', videoContext, undefined, {
		scriptUrl: videoContext.$ctx.data('src-url'),
		fnCallback: onYoutubeApiLoaded,
	});

	return {
		remove: () => {
			if (player) {
				// Youtube Video has to be paused instead of removed fully in order to keep the config
				player.pauseVideo();
			}
		},
		addEventCallback: (callbackFn) => {
			playerCallbacks.push(callbackFn);
		},
	};
};
